import {
  Component,
  computed,
  ElementRef,
  inject,
  QueryList,
  signal,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { TableModule } from 'primeng/table';
import { SkeletonModule } from 'primeng/skeleton';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { FluidModule } from 'primeng/fluid';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FloatLabelModule } from 'primeng/floatlabel';
import { TextareaModule } from 'primeng/textarea';
import { DatePickerModule } from 'primeng/datepicker';
import { ApiService } from '../../../api.service';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { TAGInvItemType, TAGInvTransaction } from '../../../../models/taginventory';
import { MessageService } from 'primeng/api';
import { PrinterService } from '../../../printer.service';
import {
  BarcodeScannerSearchField,
  CodeScannedEvent,
} from '../../../components/barcode-scanner-search-field/barcode-scanner-search-field';
import { findInvByCode } from '../inventoryHelper';
import { MatIcon } from '../../../components/mat-icon/mat-icon';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import {
  newProgressDialogStep,
  openProgressDialog,
  ProgressDialogStep,
  ProgressDialogStepState,
} from '../../../components/progressDialog/progressDialog';

export interface InventoryCheckDialogData {}

@Component({
  selector: 'app-internal-inventory-check-dialog',
  imports: [
    TableModule,
    SkeletonModule,
    InputTextModule,
    DialogModule,
    ButtonModule,
    FluidModule,
    ReactiveFormsModule,
    FloatLabelModule,
    TextareaModule,
    DatePickerModule,
    TranslatePipe,
    ToggleSwitchModule,
    BarcodeScannerSearchField,
    MatIcon,
    ProgressSpinnerModule,
    FormsModule,
  ],
  providers: [DialogService, TranslateService],
  templateUrl: './inventory-check-dialog.html',
  styleUrl: './inventory-check-dialog.scss',
})
export class InventoryCheckDialog {
  private dialogService = inject(DialogService);
  private translate = inject(TranslateService);
  private messageService = inject(MessageService);
  //private router = inject(Router);
  public api = inject(ApiService);
  public printer = inject(PrinterService);

  @ViewChild(BarcodeScannerSearchField) search: BarcodeScannerSearchField;

  @ViewChildren('instanceElements') instanceElements!: QueryList<ElementRef>;
  @ViewChildren('typeElements') typeElements!: QueryList<ElementRef>;

  transactions = signal<TAGInvTransaction[]>([]);
  loadingTransactions = signal(true);
  itemTypes = signal<TAGInvItemType[]>([]);
  itemTypesCount = signal<Record<string, number>>({});
  loadingItems = signal(true);

  collapsedTypes = signal<Record<string, boolean>>({});

  foundItems = signal<{ itemid: string; instanceid: string }[]>([]);
  returnedItems = signal<{ itemid: string; instanceid: string }[]>([]);

  scrollToItemId = signal<string | null>(null);

  filteredItems = computed(() => {
    return this.itemTypes().filter(
      (item) =>
        !item.deleted || item.instances.some((inst) => this.instanceFound(item._id, inst.itemid)),
    );
  });

  filteredInstances(item: TAGInvItemType) {
    return item.instances.filter(
      (inst) => (!inst.deleted && !item.deleted) || this.instanceFound(item._id, inst.itemid),
    );
  }

  constructor(
    public dialogRef: DynamicDialogRef<InventoryCheckDialog>,
    public config: DynamicDialogConfig<InventoryCheckDialogData>,
  ) {
    config.header = this.translate.translate('internal.inventory.inventoryCheck.title')();
  }

  ngOnInit() {
    this.api.waitForAPI().then(() => {
      this.api
        .getInvItems()
        .then((invItems: TAGInvItemType[]) => {
          this.itemTypes.set(invItems.filter((i) => i.instances.length > 0));

          this.updateCounts();

          /* if (this.transaction().allItemsBack() && this.transaction().items.length > 0) {
          // TODO: Disable Scanner Input
          this.search.disabled = true;
        } else {
          this.search.disabled = false;
          // TODO: Enable Scanner Input
        } */

          this.loadingItems.set(false);
        })
        .catch((reason) => {
          this.messageService.add({
            severity: 'error',
            summary: this.translate.translate("errors.couldNotLoadInventoryItems")(),
            detail: reason.message,
          });

          this.dialogRef.close();
        });

      this.api
        .getInvTransactions()
        .then((invTransactions: TAGInvTransaction[]) => {
          this.transactions.set(invTransactions);
          this.loadingTransactions.set(false);
        })
        .catch((reason) => {
          this.messageService.add({
            severity: 'error',
            summary: this.translate.translate("errors.couldNotLoadInventoryTransactions")(),
            detail: reason.message,
          });
        });

      let invCheckPoint = localStorage.getItem('inventoryCheckPoint');
      console.log(invCheckPoint);
      if (invCheckPoint) {
        let checkpoint = JSON.parse(invCheckPoint);

        this.foundItems.set(checkpoint.foundItems);
        this.returnedItems.set(checkpoint.returnedItems);

        this.updateCounts();
      }
    });
  }

  updateCounts() {
    this.itemTypesCount.set(
      this.itemTypes().reduce(
        (acc, item) => {
          if (this.itemComplete(item._id)) {
            const count = item.instances.length;
            acc[item._id] = count;
            this.collapsedTypes.update((ct) => {
              ct[item._id] = true;
              return Object.assign({}, ct);
            });
          } else {
            const count =
              item.instances.length -
              this.foundItems().filter((itm) => itm.itemid === item._id).length -
              this.returnedItems().filter((itm) => itm.itemid === item._id).length;
            acc[item._id] = count;
            this.collapsedTypes.update((ct) => {
              ct[item._id] = false;
              return Object.assign({}, ct);
            });
          }
          return acc;
        },
        {} as Record<string, number>,
      ),
    );
  }

  clearCheckpoint() {
    localStorage.removeItem('inventoryCheckPoint');
    this.foundItems.set([]);
    this.returnedItems.set([]);
    this.updateCounts();
  }

  transactionsForItem(itemid: string) {
    return this.transactions().filter(
      (tran) => tran.items.findIndex((comb) => comb.itemid == itemid) != -1,
    );
  }

  unfinishedTransactionsForItem(itemid: string) {
    return this.transactionsForItem(itemid).filter((tran) => !tran.allItemsBack());
  }

  instanceBooked(itemid: string, instanceid: string) {
    var transactions = this.unfinishedTransactionsForItem(itemid);

    if (transactions.length > 0) {
      for (let tran of transactions) {
        let instance = tran.findItem(itemid, instanceid);
        if (instance != null && instance.back == null) {
          return true;
        }
      }
    }

    return false;
  }

  instanceStatus(itemid: string, instanceid: string) {
    if (this.instanceFound(itemid, instanceid)) {
      return 'internal.inventory.inventoryCheck.found';
    } else if (this.instanceBooked(itemid, instanceid)) {
      return 'internal.inventory.inventoryCheck.booked';
    } else {
      return '';
    }
  }

  instanceFound(itemid: string, instanceid: string) {
    let found =
      this.foundItems().findIndex(
        (comb) => comb.itemid == itemid && comb.instanceid == instanceid,
      ) != -1;
    found =
      found ||
      this.returnedItems().findIndex(
        (comb) => comb.itemid == itemid && comb.instanceid == instanceid,
      ) != -1;

    return found;
  }

  itemComplete(itemid: string) {
    var complete = true;
    var item = this.itemTypes().find((item) => item._id == itemid);

    for (let instance of item.instances) {
      complete = complete && this.instanceFound(itemid, instance.itemid);
    }

    return complete;
  }

  instanceDeleted(itemid: string, instanceid: string) {
    var item = this.itemTypes().find((itm) => itm._id == itemid);
    if (!item) return false;

    var instance = item.instances.find((inst) => inst.itemid == instanceid);
    if (!instance) return false;

    return item.deleted || instance.deleted;
  }

  async save() {
    console.log(this.returnedItems());
    console.log(this.transactions());

    // Book Back found Items that were booked somewhere
    let backTransactions: { [key: string]: { itemid: string; instanceid: string }[] } = {};
    for (let item of this.returnedItems()) {
      let transactions = this.unfinishedTransactionsForItem(item.itemid);

      console.log(transactions);

      if (transactions.length > 0) {
        for (let tran of transactions) {
          let instance = tran.findItem(item.itemid, item.instanceid);
          if (instance != null && instance.back == null) {
            if (backTransactions[tran._id] == undefined) backTransactions[tran._id] = [];
            backTransactions[tran._id].push({ itemid: item.itemid, instanceid: item.instanceid });
          }
        }
      } else {
        console.error(
          'No unfinished transaction found for booked item ' +
            item.itemid +
            ' instance ' +
            item.instanceid,
        );
      }
    }

    console.log(backTransactions);
    console.log(Object.keys(backTransactions));

    for (let tranid of Object.keys(backTransactions)) {
      console.log('Returning Items back of Transaction ' + tranid + ' ...');
      console.log(backTransactions[tranid]);
      try {
        let result = await this.api.returnTransactionItems(tranid, backTransactions[tranid]);
        console.log('Returned Items of Transaction ' + tranid);
      } catch (err) {
        console.error(err);
        this.messageService.add({
          severity: 'error',
          summary: this.translate.translate("internal.inventory.transactionEdit.couldNotReturnItem")(),
          detail: err.message,
        });
      }
    }

    // Book out Items that were not found
    var trans = new TAGInvTransaction();
    trans.startdate = new Date();
    trans.location = 'Inventory Check';

    var notFoundItems: { itemid: string; instanceid: string; back: Date }[] = [];
    for (let item of this.itemTypes().filter((item) => !item.deleted)) {
      for (let instance of item.instances.filter((inst) => !inst.deleted)) {
        if (
          !this.instanceFound(item._id, instance.itemid) &&
          !this.instanceBooked(item._id, instance.itemid)
        ) {
          notFoundItems.push({ itemid: item._id, instanceid: instance.itemid, back: null });
        }
      }
    }

    trans.items = notFoundItems;

    if (notFoundItems.length > 0) {
      let createStep = newProgressDialogStep(
        new ProgressDialogStep(
          ProgressDialogStepState.WAITING,
          this.translate.translate('internal.inventory.inventoryCheck.createProgress')(),
        ),
      );
      let progressDialog = openProgressDialog(this.dialogService, [createStep]);

      progressDialog.onChildComponentLoaded.subscribe((pDialog) => {
        createStep.update((s) => s.setState(ProgressDialogStepState.PROCESSING));

        this.api
          .createTransaction(trans)
          .then(async (result) => {
            this.clearCheckpoint();
            createStep.update((s) => s.setState(ProgressDialogStepState.SUCCESS));
            await pDialog.finishDialog();
            this.dialogRef.close(true);
          })
          .catch((reason) => {
            createStep.update((s) => s.setState(ProgressDialogStepState.ERROR));
            createStep.update((s) => s.setSubtitle(reason.message));
            pDialog.finishDialog(-1);
            console.error(reason);
          });
      });
    } else {
      this.dialogRef.close(null);
    }
  }

  async codeScanned(code: CodeScannedEvent) {
    this.search.busy = true;

    let { item, instance } = await findInvByCode(
      code.code,
      undefined,
      undefined,
      undefined,
      this.api,
    );

    if (item) {
      if (instance) {
        await this.itemScanned(item._id, instance.itemid);
      } else {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.translate('internal.inventory.general.noInstanceID')(),
        });
      }
    } else {
      try {
        let group = await this.api.getInvItemGroup(code.code);
        if (group) {
          if (this.api.config().allowGroupsForBookIn) {
            for (let gItem of group.items) {
              await this.itemScanned(gItem.itemid, gItem.instanceid);
            }
          } else {
            this.messageService.add({
              severity: 'error',
              summary: this.translate.translate(
                'internal.inventory.general.groupForbidden',
              )(),
            });
          }
        } else {
          this.messageService.add({
            severity: 'error',
            summary: this.translate.translate("internal.inventory.inventoryCheck.notFound")(),
            detail: this.translate.translate("internal.inventory.general.couldNotFindItemOrGroup")(),
          });
        }
      } catch (reason) {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.translate("internal.inventory.inventoryCheck.notFound")(),
          detail: reason.message,
        });
      }
    }

    this.search.busy = false;
    this.search.reset();
  }

  async itemScanned(itemID: string, instID: string) {
    // Check if item is even in the items array
    if (!this.itemTypes().some((item) => item._id == itemID)) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.translate('internal.inventory.general.notInTransaction')(),
      });
      return;
    }

    // Check if instance is in item
    if (
      !this.itemTypes()
        .find((item) => item._id == itemID)
        ?.instances.some((inst) => inst.itemid == instID)
    ) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.translate('internal.inventory.general.couldNotFindItemOrGroup')(),
      });
      return;
    }

    var found = this.instanceFound(itemID, instID);
    console.log('Instance complete: ' + found);

    if (!found) {
      let booked = this.instanceBooked(itemID, instID);
      console.log('Instance booked: ' + booked);

      if (booked) {
        this.returnedItems.update((ri) => [...ri, { itemid: itemID, instanceid: instID }]);
        this.scrollToItemId.set(itemID + '-' + instID);
        this.scrollToItem();
        this.messageService.add({
          severity: 'success',
          summary: this.translate.translate(
            'internal.inventory.inventoryCheck.removedFromBooking',
          )(),
        });
      } else {
        this.foundItems.update((fi) => [...fi, { itemid: itemID, instanceid: instID }]);
        this.scrollToItemId.set(itemID + '-' + instID);
        this.scrollToItem();
        this.messageService.add({
          severity: 'success',
          summary: this.translate.translate('internal.inventory.inventoryCheck.found')(),
        });
      }

      if (this.itemComplete(itemID)) {
        this.collapsedTypes.update((ct) => {
          ct[itemID] = true;
          return Object.assign({}, ct);
        });
        this.updateCounts();
      } else {
        this.collapsedTypes.update((ct) => {
          ct[itemID] = false;
          return Object.assign({}, ct);
        });
        this.updateCounts();
      }

      localStorage.setItem(
        'inventoryCheckPoint',
        JSON.stringify({ foundItems: this.foundItems(), returnedItems: this.returnedItems() }),
      );
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.translate('internal.inventory.general.alreadyCheckedIn')(),
      });
    }
  }

  toggleCollapse(typeId: string): void {
    this.collapsedTypes.update((ct) => {
      ct[typeId] = !ct[typeId];
      return Object.assign({}, ct);
    });
  }

  printLabel(item: TAGInvItemType, instance: { itemid: string }) {
    if (!this.printer.isConnected())
      this.printer
        .connectToPrinter()
        .then((result) => {
          this.printer.printItemLabels(item, [instance]);
        })
        .catch((reason) => {
          this.messageService.add({
            severity: 'error',
            summary: this.translate.translate('errors.couldNotConnectToPrinter')(),
            detail: reason.message,
          });
        });
    else this.printer.printItemLabels(item, [instance]);
  }

  scrollToItem() {
    if (!this.scrollToItemId()) return;

    // Item Type
    if (this.scrollToItemId().indexOf('-') === -1) {
      const target = this.typeElements
        .toArray()
        .find((el) => el.nativeElement.id === this.scrollToItemId());

      if (target) {
        target.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      // Remove the shaking after 2 seconds
      setTimeout(() => {
        this.scrollToItemId.set(null);
      }, 2000);
    } else {
      //Item Instance
      const target = this.instanceElements
        .toArray()
        .find((el) => el.nativeElement.id === this.scrollToItemId());

      console.log('Found target: ', target);
      if (target) {
        if (this.collapsedTypes()[this.scrollToItemId().split('-')[0]]) {
          this.collapsedTypes.update((ct) => {
            ct[this.scrollToItemId().split('-')[0]] = false;
            return Object.assign({}, ct);
          });

          //this.cdr.detectChanges();  // Manually trigger change detection
          setTimeout(() => {
            target.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 100);
        } else {
          console.log('Scrolling to ' + target.nativeElement.id);
          target.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }

        // Remove the shaking after 2 seconds
        setTimeout(() => {
          this.scrollToItemId.set(null);
        }, 2000);
      } else {
        console.error('Element not found', this.scrollToItemId());
      }
    }
  }

  instanceShaking(itemid: string, instanceid: string) {
    return itemid + '-' + instanceid === this.scrollToItemId();
  }

  typeShaking(itemid: string) {
    return itemid === this.scrollToItemId();
  }
}
